import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { tasksAPI } from '../services/api';
import { Task } from '../types';
import TaskDetailSidebar from './TaskDetailSidebar';
import TaskModal from './TaskModal';
import { showCustomAlert } from './CustomAlert';
import { useAuth } from '../contexts/AuthContext';

interface ProjectTasksBoardProps {
  projectId: string;
}

type TaskStatus = 'todo' | 'in_progress' | 'done';

const ProjectTasksBoard: React.FC<ProjectTasksBoardProps> = ({ projectId }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);

  useEffect(() => {
    loadTasks();
  }, [projectId]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await tasksAPI.getAll({ projectId });
      const tasksList = Array.isArray(data) ? data : (data as any).items || [];
      setTasks(tasksList);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setTaskModalVisible(true);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
  };

  const canChangeStatus = (task: Task) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'direktor') return true;
    return task.assignees?.some((assignee) => assignee._id === user._id) || false;
  };

  const canEditTask = (task: Task) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'direktor') return true;
    return task.createdBy?._id === user._id;
  };

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    if (!canChangeStatus(task)) {
      e.preventDefault();
      return;
    }
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: TaskStatus) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.status === targetStatus) {
      setDraggedTask(null);
      return;
    }

    try {
      await tasksAPI.updateStatus(draggedTask._id, targetStatus);
      await loadTasks();
    } catch (err: any) {
      console.error('Error updating task status:', err);
    } finally {
      setDraggedTask(null);
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setTaskModalVisible(true);
  };

  const handleDeleteTask = async (task: Task) => {
    showCustomAlert.confirm(
      'Da li ste sigurni da želite da obrišete zadatak?',
      async () => {
        try {
          await tasksAPI.delete(task._id);
          await loadTasks();
        } catch (err: any) {
          console.error('Error deleting task:', err);
          showCustomAlert.error('Greška pri brisanju zadatka');
        }
      },
      'Potvrda brisanja'
    );
  };

  const getTasksByStatus = (status: TaskStatus) => {
    return tasks.filter(task => task.status === status);
  };

  const statusConfig: Record<TaskStatus, { label: string; color: string }> = {
    todo: { label: 'ZA RAD', color: '#3b82f6' },
    in_progress: { label: 'U TOKU', color: '#f59e0b' },
    done: { label: 'ZAVRŠENO', color: '#10b981' }
  };

  const priorityColors: Record<string, string> = {
    low: '#6b7280',
    medium: '#f59e0b',
    high: '#ef4444'
  };

  const priorityLabels: Record<string, string> = {
    low: 'Nizak',
    medium: 'Srednji',
    high: 'Visok'
  };

  const renderTaskCard = (task: Task) => {
    return (
      <div
        key={task._id}
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        onClick={() => handleTaskClick(task)}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '0.75rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          border: '1px solid #e5e7eb',
          transition: 'all 0.2s',
          opacity: draggedTask?._id === task._id ? 0.5 : 1
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>{task.name}</h4>
          <Tag
            value={priorityLabels[task.priority]}
            severity={task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warning' : 'info'}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          />
        </div>
        
        {task.description && (
          <p style={{ fontSize: '0.875rem', color: '#666', margin: '0.5rem 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {task.description.length > 100 ? `${task.description.substring(0, 100)}...` : task.description}
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <i className="pi pi-users" style={{ fontSize: '0.875rem', color: '#666' }} />
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {task.assignees.slice(0, 4).map((assignee) => (
              assignee.avatarUrl ? (
                <img
                  key={assignee._id}
                  src={assignee.avatarUrl}
                  alt={`${assignee.firstName} ${assignee.lastName}`}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '1px solid #e5e7eb'
                  }}
                  title={`${assignee.firstName} ${assignee.lastName}`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.style.cssText = 'width: 24px; height: 24px; border-radius: 50%; background-color: #9ca3af; color: white; display: flex; align-items: center; justify-content: center; font-size: 0.625rem; font-weight: bold;';
                    fallback.textContent = `${assignee.firstName[0]}${assignee.lastName[0]}`;
                    target.parentNode?.appendChild(fallback);
                  }}
                />
              ) : (
                <div
                  key={assignee._id}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#9ca3af',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.625rem',
                    fontWeight: 'bold'
                  }}
                  title={`${assignee.firstName} ${assignee.lastName}`}
                >
                  {assignee.firstName[0]}{assignee.lastName[0]}
                </div>
              )
            ))}
            {task.assignees.length > 4 && (
              <span style={{ fontSize: '0.75rem', color: '#666' }}>+{task.assignees.length - 4}</span>
            )}
          </div>
        </div>

        {task.deadline && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
            <i className="pi pi-calendar" />
            <span>{new Date(task.deadline).toLocaleDateString('sr-RS')}</span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
          {canEditTask(task) ? (
            <>
              <i
                className="pi pi-pencil"
                style={{ 
                  color: '#6b7280', 
                  cursor: 'pointer', 
                  fontSize: '0.875rem',
                  transition: 'opacity 0.2s'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditTask(task);
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                title="Izmeni"
              />
              <i
                className="pi pi-trash"
                style={{ 
                  color: '#6b7280', 
                  cursor: 'pointer', 
                  fontSize: '0.875rem',
                  transition: 'opacity 0.2s'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTask(task);
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                title="Obriši"
              />
            </>
          ) : (
            <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          label="Add task"
          icon="pi pi-plus"
          className="p-button-success"
          onClick={handleAddTask}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', minHeight: '500px' }}>
        {(['todo', 'in_progress', 'done'] as TaskStatus[]).map((status) => {
          const config = statusConfig[status];
          const statusTasks = getTasksByStatus(status);
          
          return (
            <div
              key={status}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}
              style={{
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                padding: '1rem',
                minHeight: '400px',
                border: '2px dashed transparent',
                transition: 'all 0.2s'
              }}
              onDragEnter={(e) => {
                if (draggedTask && draggedTask.status !== status) {
                  e.currentTarget.style.borderColor = config.color;
                  e.currentTarget.style.backgroundColor = `${config.color}15`;
                }
              }}
              onDragLeave={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: config.color
                  }}
                />
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: config.color }}>
                  {config.label}
                </h3>
                <Tag
                  value={statusTasks.length}
                  severity="info"
                  style={{ marginLeft: 'auto', fontSize: '0.75rem' }}
                />
              </div>
              
              <div style={{ minHeight: '300px' }}>
                {statusTasks.map(task => renderTaskCard(task))}
                {statusTasks.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                    Nema zadataka
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedTask && (
        <TaskDetailSidebar
          task={selectedTask}
          visible={!!selectedTask}
          onHide={() => setSelectedTask(null)}
          onTaskUpdate={loadTasks}
        />
      )}

      {taskModalVisible && (
        <TaskModal
          visible={taskModalVisible}
          onHide={() => {
            setTaskModalVisible(false);
            setEditingTask(null);
          }}
          projectId={projectId}
          task={editingTask}
          onSave={loadTasks}
        />
      )}
    </div>
  );
};

export default ProjectTasksBoard;
